<template>
    <div v-loading="loading" class="user-portfolio-career">
        <div class="user-portfolio-career__head">
            <div class="user-portfolio-career__head-title">
                Карьера
            </div>
            <div
                v-if="getUser.isNoWorkExperience"
                class="user-portfolio-career__head-experience user-portfolio-career__head-experience--empty"
            >
                <strong>Нет опыта работы</strong>
            </div>
            <div
                v-else-if="sortedCareers.length === 0"
                class="user-portfolio-career__head-experience user-portfolio-career__head-experience--not-filled"
            >
                <strong>Не указано</strong>
            </div>
            <div v-else class="user-portfolio-career__head-experience">
                <span>Опыт работы:</span>
                <strong>{{ experience }}</strong>
            </div>
        </div>

        <div
            v-if="getUser.keySkills && getUser.keySkills.length"
            class="user-portfolio-career__key-skills"
        >
            <div class="user-portfolio-career__key-skills-title">
                Профессиональные навыки
            </div>
            <div class="user-portfolio-career__key-skills-items">
                <div
                    v-for="(skill, index) in getUser.keySkills"
                    class="user-portfolio-career__key-skills-item"
                    :key="index"
                >
                    {{ skill.title || getSkillName(skill.rsvDataId) }}
                </div>
            </div>
        </div>

        <div
            v-if="getUser.activity && getUser.activity.length"
            class="user-portfolio-career__activity"
        >
            <div class="user-portfolio-career__activity-title">
                Сфера деятельности:
            </div>
            <div class="user-portfolio-career__activity-items">
                {{ activities }}
            </div>
        </div>

        <div
            v-if="!getUser.isNoWorkExperience && getUser.isGovernmentWorker"
            class="user-portfolio-career__government-work"
        >
            Есть опыт работы на государственной службе
        </div>

        <div
            v-if="sortedCareers.length"
            class="user-portfolio-career__work-places"
        >
            <div
                v-for="(career, index) in sortedCareers"
                :key="index"
                class="user-portfolio-career__work-place"
            >
                <div class="user-portfolio-career__work-place-date">
                    <div v-if="career.dateStart">{{ getDateString(career.dateStart) }}</div>

                    <div v-if="isMobile && career.dateStart && (career.isUntilNow || career.dateEnd)">-</div>

                    <div v-if="career.isUntilNow">По настоящее время</div>
                    <div v-else-if="career.dateEnd">{{ getDateString(career.dateEnd) }}</div>

                    <div v-if="!career.isUntilNow && !career.dateStart && !career.dateEnd">
                        Период не указан
                    </div>
                </div>
                <div class="user-portfolio-career__work-place-info">
                    <div
                        class="user-portfolio-career__work-place-info-organization"
                        v-html="getHighlightHtml(career.organisation)"
                    />
                    <div
                        class="user-portfolio-career__work-place-info-position"
                        v-html="getHighlightHtml(career.position)"
                    />
                    <div
                        v-if="career.responsibilities && opened"
                        class="user-portfolio-career__work-place-info-responsibilities"
                        v-html="getHighlightHtml(career.responsibilities).replace(/(\r\n|\n|\r)/g, '<br/>')"
                    />
                </div>
                <div
                    class="user-portfolio-career__work-place-dot"
                    :class="{
                        'user-portfolio-career__work-place-dot--current': career.isUntilNow,
                    }"
                ></div>
            </div>
        </div>

        <button
            v-if="sortedCareers.length"
            type="button"
            class="user-portfolio-career__toggle"
            :class="{
                'user-portfolio-career__toggle--opened': opened,
            }"
            v-text="opened ? 'Свернуть опыт' : 'Развернуть опыт'"
            @click="togglePlaces"
        ></button>
    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import str_counter from "@/tools/str_counter";
import moment from 'moment';
import {getHighlightGroupsKeywordsSet, highlightStrKeywords} from "@/tools/graphQueryHelpers";

const strCounterYears = str_counter('', 'год', 'года', 'лет');
const strCounterMonths = str_counter('месяц', '', 'а', 'ев');

export default {
    props: {
        highlights: {
            type: Object,
            required: false,
            default: () => null,
        },
    },

    data: () => ({
        loading: false,
        opened: false,
    }),

    async created() {
        this.loading = true;

        await Promise.all([
            this.loadRegistryItems({ type: 'key_skills' }),
            this.loadRegistryItems({ type: 'industries' }),
            this.loadUserCareers(this.getUser.userId),
        ]);

        this.loading = false;
    },

    watch: {
        async 'getUser.userId'(userId) {
            this.loading = true;

            await this.loadUserCareers(userId);

            this.loading = false;
        },
    },

    computed: {
        ...mapGetters([
            'isMobile',
        ]),
        ...mapGetters('newUsers', [
            'getUser',
            'getUserCareers',
        ]),
        ...mapGetters('data', [
            'getRegistryItems',
            'getRegistryValueById',
        ]),

        highlightKeywords() {
            return getHighlightGroupsKeywordsSet(this.highlights, [
                'career.organization',
                'career.position',
                'career.responsibilities',
            ]);
        },

        experience() {
            const expDays = this.getUser.workExperience;

            if (!expDays || this.getUser.isNoWorkExperience) {
                return 'Нет данных';
            }

            const monthAverage = 30.4375;
            const years = Math.floor( expDays / (monthAverage * 12) );
            const months = Math.floor( (expDays - (years * 12 * monthAverage)) / monthAverage );

            const data = [
                years ? `${years} ${strCounterYears(years)}` : null,
                months ? `${months} ${strCounterMonths(months)}` : null,
            ];

            return data.filter(v => v).join(' ');
        },

        sortedCareers() {
            if (this.getUser.isNoWorkExperience) {
                return [];
            }

            return (this.getUserCareers || [])
                .filter(v => v.organisation && v.position)
                .sort((a, b) => {
                    if ((a.isUntilNow || b.isUntilNow) && !(a.isUntilNow && b.isUntilNow)) {
                        return a.isUntilNow ? -1 : 1;
                    }

                    if ((!a.dateStart || !b.dateStart) && !(!a.dateStart && !b.dateStart)) {
                        return a.dateStart ? -1 : 1;
                    }

                    const startDiff = moment(a.dateStart || new Date()).diff(b.dateStart || new Date());

                    if (startDiff !== 0) {
                        return startDiff > 0 ? -1 : 1;
                    }

                    if (!a.dateEnd || !b.dateEnd) {
                        return !a.dateEnd && !b.dateEnd ? 0 : a.dateEnd ? -1 : 1;
                    }

                    const endDiff = moment(a.dateEnd || new Date()).diff(b.dateEnd || new Date());

                    return endDiff === 0 ? 0 : endDiff > 0 ? -1 : 1;
                })
            ;
        },

        activities() {
            const data = this.getUser.activity.map(activity => this.getRegistryValueById(activity));
            return data.join(", ");
        },
    },

    methods: {
        ...mapActions('newUsers', [
            'loadUserCareers',
        ]),
        ...mapActions('data', [
            'loadRegistryItems',
        ]),

        getSkillName(id) {
            const skill = this.getRegistryItems('key_skills').find(v => v.id === id);
            return skill ? skill.field_value : '';
        },

        getDateString(date) {
            const dateStr = moment(date).format('MMMM YYYY');
            return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
        },

        togglePlaces() {
            this.opened = !this.opened;
        },

        getHighlightHtml(str) {
            return str ? highlightStrKeywords(str, this.highlightKeywords) : str;
        },
    },
};

</script>

<style scoped lang="scss">
@import "~@/styles/rem";
@import '~@/styles/variables';

.user-portfolio-career {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-content: stretch;
    align-items: flex-start;
    gap: mrem(40);

    @include on-desktop {
        gap: rem(40);
    }

    &__head {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;

        @include on-desktop {
            flex-direction: row;
            align-items: center;
        }

        &-title {
            color: $color-main-blue;
            font-weight: 700;
            font-size: mrem(21);
            line-height: mrem(30);

            @include on-desktop {
                font-size: rem(21);
                line-height: rem(30);
            }
        }

        &-experience {
            display: flex;
            flex-direction: row;
            align-items: center;
            font-weight: 400;
            gap: mrem(6);

            @include on-desktop {
                gap: rem(6);
            }

            strong {
                font-weight: 600;
                font-size: mrem(18);
                line-height: mrem(24);

                @include on-desktop {
                    font-size: rem(18);
                    line-height: rem(24);
                }
            }

            &--empty, &--not-filled {
                color: $color-main-grey;
                font-weight: 500;
                font-size: rem(14);
                line-height: rem(20);

                strong {
                    font-weight: inherit;
                }
            }
        }
    }

    &__key-skills {
        width: 100%;

        .user-portfolio-career__key-skills-title {
            font-weight: 700;
            font-size: mrem(18);
            line-height: mrem(24);
            margin-bottom: mrem(16);

            @include on-desktop {
                font-size: rem(18);
                line-height: rem(24);
                margin-bottom: rem(16);
            }
        }
        .user-portfolio-career__key-skills-items {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            gap: mrem(16);

            @include on-desktop {
                gap: rem(16);
            }

            .user-portfolio-career__key-skills-item {
                color: $color-main-blue;
                font-weight: 400;
                display: flex;
                justify-content: center;
                align-items: center;
                border: 1px solid $color-main-blue;

                gap: mrem(8);
                padding: mrem(8) mrem(18);
                border-radius: mrem(120);

                @include on-desktop {
                    gap: rem(8);
                    padding: rem(8) rem(18);
                    border-radius: rem(120);
                }
            }
        }
    }

    &__activity {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: mrem(16);

        @include on-desktop {
            flex-direction: row;
            gap: rem(16);
        }

        .user-portfolio-career__activity-title {
            flex: 0 0 auto;
            font-weight: 700;
            font-size: mrem(18);
            line-height: mrem(24);

            @include on-desktop {
                font-size: rem(18);
                line-height: rem(24);
            }
        }
        .user-portfolio-career__activity-items {
            font-weight: 400;
        }
    }

    &__government-work {
        flex: 0 0 auto;
        width: auto;
        display: inline-block;
        background: $color-main-notice-light;
        font-weight: 600;
        padding: mrem(8) mrem(10);
        gap: mrem(10);
        border-radius: mrem(16);

        @include on-desktop {
            padding: rem(8) rem(10);
            gap: rem(10);
            border-radius: rem(120);
        }
    }

    &__work-places {
        display: flex;
        flex-direction: column;
        gap: mrem(40);

        @include on-desktop {
            gap: rem(40);
        }

        .user-portfolio-career__work-place {
            display: grid;
            position: relative;
            grid-template-columns: auto;
            gap: mrem(4);
            padding-left: mrem(22);

            @include on-desktop {
                grid-template-columns: rem(180) auto;
                gap: rem(24);
                padding-left: rem(22);
            }

            &-dot {
                position: absolute;
                z-index: 2;
                border-radius: 100%;
                left: 0;
                background: url("~@/assets/images/portfolio/dot-list.svg") no-repeat center center;
                background-size: contain;
                background-color: #fff;

                width: mrem(16);
                height: mrem(16);
                top: mrem(2);

                @include on-desktop {
                    width: rem(16);
                    height: rem(16);
                    top: rem(2);
                }

                &--current {
                    background-image: url("~@/assets/images/portfolio/dot-list--active.svg");
                }
            }

            &:after {
                z-index: 1;
                content: '';
                position: absolute;
                background: $color-main-blue;
                width: mrem(1);
                height: calc(100% + #{mrem(45)});
                left: mrem(7.5);
                top: mrem(8);

                @include on-desktop {
                    width: rem(1);
                    height: calc(100% + #{rem(45)});
                    left: rem(7.5);
                    top: rem(8);
                }
            }

            &:last-of-type {
                &:after {
                    display: none;
                }
            }

            &-date {
                display: flex;
                flex-direction: row;
                gap: mrem(4);
                color: $color-main-grey;
                font-weight: 600;
                font-size: mrem(14);
                line-height: mrem(20);

                @include on-desktop {
                    display: block;
                    font-size: rem(14);
                    line-height: rem(20);
                }
            }

            &-info {
                display: flex;
                flex-direction: column;
                gap: mrem(6);

                @include on-desktop {
                    gap: rem(6);
                }

                &-organization {
                    font-weight: 700;
                }
                &-position {
                    font-weight: 500;
                }
                &-responsibilities {
                    font-weight: 400;
                    padding-top: mrem(4);
                    font-size: mrem(14);
                    line-height: mrem(20);

                    @include on-desktop {
                        padding-top: rem(4);
                        font-size: rem(14);
                        line-height: rem(20);
                    }
                }
            }
        }
    }

    &__toggle {
        font-family: $font-montserrat;
        width: calc(100% + #{mrem(40)});
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        align-content: center;
        border: none;
        background: $color-main-notice-lighter;
        cursor: pointer;
        font-weight: 400;
        margin: mrem(-20);
        padding: mrem(20);
        gap: mrem(6);
        border-radius: 0 0 mrem(16) mrem(16);
        font-size: mrem(16);
        line-height: mrem(22);

        @include on-desktop {
            width: calc(100% + #{rem(40)});
            margin: rem(-20);
            padding: rem(20);
            gap: rem(6);
            border-radius: 0 0 rem(16) rem(16);
            font-size: rem(16);
            font-weight: 400;
            line-height: rem(22);
        }

        &::after {
            content: '';
            display: inline-block;
            background: url("~@/assets/images/portfolio/chevron.svg") no-repeat center center;
            background-size: contain;
            width: mrem(24);
            height: mrem(24);

            @include on-desktop {
                width: rem(24);
                height: rem(24);
            }
        }

        &--opened {
            &::after {
                transform: rotate(180deg);
            }
        }
    }
}

</style>
